import { ThemeEvents } from '@theme/events';

class ProductMediaGallery extends HTMLElement {
  connectedCallback() {
    // Surbrillance « live » des vignettes pendant le glissement (swipe).
    // Splide ne met à jour la classe .is-active (donc le contour de la vignette
    // active) qu'au RELÂCHEMENT du glissement : pendant le drag, l'image
    // principale suit le doigt et affiche déjà la diapo suivante, mais le contour
    // reste sur l'ancienne → « le contour a du retard sur l'image ». On calcule
    // ici, en temps réel, la diapo la plus proche à partir du décalage (transform)
    // de la piste principale, et on repositionne le contour immédiatement. Au
    // relâchement, Splide reprend la main et fixe l'état final (cohérent).
    // Écouteurs délégués sur l'élément galerie (persistant) : survivent au
    // remplacement du slider lors d'un changement de variante.
    this.#setupLiveThumbnailHighlight();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdated, this.updateMediaGallery);
  }

  disconnectedCallback() {
    if (this.dragging) this.dragging = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    window.removeEventListener('pointerup', this.stopDragHighlight);
    window.removeEventListener('pointercancel', this.stopDragHighlight);

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdated, this.updateMediaGallery);
  }

  #setupLiveThumbnailHighlight() {
    this.dragging = false;
    this.rafId = null;

    // Phase de CAPTURE : Splide/slider-component consomme les évènements pointer
    // du glissement avant qu'ils ne remontent (bubbling) jusqu'à cet élément. En
    // écoutant en capture (de la racine vers la cible), on les reçoit toujours,
    // quelle que soit la gestion de propagation en aval.
    this.addEventListener(
      'pointerdown',
      (event) => {
        const mainSlider = this.querySelector('[data-ref="main-slider"]');
        if (mainSlider && mainSlider.contains(event.target)) this.dragging = true;
      },
      { capture: true, passive: true }
    );

    this.addEventListener(
      'pointermove',
      () => {
        if (!this.dragging || this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.#highlightClosestThumbnail();
        });
      },
      { capture: true, passive: true }
    );

    window.addEventListener('pointerup', this.stopDragHighlight, { passive: true });
    window.addEventListener('pointercancel', this.stopDragHighlight, { passive: true });
  }

  stopDragHighlight = () => {
    this.dragging = false;
  };

  #highlightClosestThumbnail() {
    const list = this.querySelector('[data-ref="main-slider"] .splide__list');
    const thumbnails = this.querySelectorAll('[data-ref="thumbnail-slider"] .splide__slide');
    if (!list || thumbnails.length < 2) return;

    const firstSlide = list.children[0];
    if (!firstSlide) return;

    const styles = getComputedStyle(list);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const step = firstSlide.getBoundingClientRect().width + gap;
    if (!step) return;

    const translateX = new DOMMatrixReadOnly(styles.transform).m41;
    let index = Math.round(-translateX / step);
    index = Math.max(0, Math.min(thumbnails.length - 1, index));

    thumbnails.forEach((thumbnail, position) => {
      thumbnail.classList.toggle('is-active', position === index);
    });
  }

  updateMediaGallery = (event) => {
    const newMediaGallery = event.detail.data.html.querySelector('product-media-gallery slider-component');
    const currentMediaGallery = this.querySelector('slider-component');

    if (!newMediaGallery || !currentMediaGallery) return;

    if (currentMediaGallery.innerHTML !== newMediaGallery.innerHTML) {
      currentMediaGallery.replaceWith(newMediaGallery);
    }
  };
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}
